import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SiaComponentConfigApi } from '../apis/sia-component-config-api';
import { PerspectiveLevelDialogComponent } from '../dialogs/perspective-level-dialog/perspective-level-dialog.component';
import { PerspectiveProductionLevelDialogComponent } from '../dialogs/perspective-production-level-dialog/perspective-production-level-dialog.component';
import { YesNoDialogComponent } from '../dialogs/yes-no-dialog/yes-no-dialog';
import { SiaComponentConfigPerspectives } from '../interfaces/sia-component-config-perspectives';
import { SiaCustomControlConfiguration } from '../interfaces/sia-template-configuration';
import { EnvironmentService } from './envirorment.service';

@Injectable({
  providedIn: 'root',
})
export class SiaComponentConfigService {
  private api = inject(SiaComponentConfigApi);
  private matDialog = inject(MatDialog);
  environmentService = inject(EnvironmentService);

  async isModuleEnabled(moduleId: string): Promise<boolean> {
    try {
      return await this.api.isModuleEnabled(moduleId);
    } catch (error) {
      console.error(`Error checking if module ${moduleId} is enabled:`, error);
      return false;
    }
  }
  getComponentPerspectives(
    type: string,
    componentId: string,
  ): Promise<SiaComponentConfigPerspectives[] | null> {
    if (!componentId) {
      return Promise.resolve(null);
    }
    return this.api.getComponentPerspectives(type, componentId);
  }
  getPerspective(id: string) {
    return this.api.getPerspective(id);
  }

  getPerspectives(type: string) {
    return this.api.getPerspectives(type);
  }

  getPerspectivesInfo(type: string) {
    return this.api.getPerspectivesInfo(type);
  }

  getCustomControlUsages(
    customControlId: string,
  ): Promise<SiaComponentConfigPerspectives[]> {
    return this.api.getCustomControlUsages(customControlId);
  }

  deleteCustomControl(customControlId: string): Promise<boolean> {
    return this.api.deleteCustomControl(customControlId);
  }

  updateCustomControlConfiguration(
    customControlId: string | undefined,
    description: string,
    newJson: string,
    configType?: string,
    templateType?: string,
  ): Promise<SiaCustomControlConfiguration> {
    var conf: SiaCustomControlConfiguration = {
      id: customControlId,
      description: description,
      customControlType: templateType ?? '',
      jsonConfig: newJson,
      configType: configType ?? '',
    };
    return this.api.updateCustomControlConfiguration(conf);
  }

  getCustomControl(customControlId: string) {
    return this.api.getCustomControl(customControlId);
  }

  async setComponentPerspective(
    componentPerspective: SiaComponentConfigPerspectives,
  ): Promise<SiaComponentConfigPerspectives> {
    if (
      componentPerspective.isPrivate ||
      componentPerspective.isPrivateAndSingle
    ) {
      return this.api.setComponentPerspective({
        ...componentPerspective,
        id: undefined,
        defaultMode: componentPerspective.defaultMode ?? 'a',
        isPrivate: true,
      });
    }
    const dialogRef = this.matDialog.open(PerspectiveLevelDialogComponent, {
      data: {
        configuration: componentPerspective,
        update: false,
        iscore: this.environmentService.production ? false : true,
      },
      disableClose: true,
    });
    const updatedLevelConf = (await firstValueFrom(
      dialogRef.afterClosed(),
    )) as SiaComponentConfigPerspectives;
    const updatedConf =
      await this.api.setComponentPerspective(updatedLevelConf);
    return updatedConf;
  }

  async updateComponentPerspective(
    conf: SiaComponentConfigPerspectives,
  ): Promise<SiaComponentConfigPerspectives> {
    if (conf.isPrivateAndSingle) {
      return this.api.setComponentPerspective(conf);
    }
    if (conf.isPrivate) {
      return this.api.updateComponentPerspective(conf);
    }
    let dialogRef: any = null;
    if (this.environmentService.production) {
      conf.deleteDelta = true;
      conf.isDelta = false;
      dialogRef = this.matDialog.open(
        PerspectiveProductionLevelDialogComponent,
        {
          data: {
            iscore: conf.levelType == 'core',
            configuration: { ...conf, levelType: 'customer' },
          },
          disableClose: true,
        },
      );
    } else {
      dialogRef = this.matDialog.open(PerspectiveLevelDialogComponent, {
        data: {
          configuration: conf,
          update: true,
          iscore: conf.levelType == 'core',
        },
        disableClose: true,
      });
    }
    const updatedLevelConf = (await firstValueFrom(
      dialogRef.afterClosed(),
    )) as SiaComponentConfigPerspectives;
    if (!updatedLevelConf) {
      return conf;
    }
    const updatedConf =
      await this.api.updateComponentPerspective(updatedLevelConf);
    return updatedConf;
  }

  updateComponentPerspectiveInfo(data: SiaComponentConfigPerspectives) {
    return this.api.updateComponentPerspectiveInfo(data);
  }

  async deletePerspective(id: string): Promise<boolean> {
    const perspectiveToDelete: SiaComponentConfigPerspectives =
      await this.getPerspective(id);
    if (
      perspectiveToDelete!.defaultMode === 'A' &&
      perspectiveToDelete!.levelType !== 'private'
    ) {
      const dialogRef = this.matDialog.open(YesNoDialogComponent, {
        data: {
          title: 'Sicuro di voler eliminare la prospettiva di default ?',
          negativeMeaning: true,
        },
      });
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) {
        return false;
      }
    }
    return this.api.deletePerspective(id);
  }

  getDefaultPerspective(
    type: string,
    componentId: string,
    deviceType: string = 'MASTER',
  ): Promise<SiaComponentConfigPerspectives | null> {
    if (!componentId) {
      return Promise.resolve(null);
    }
    return this.api.getDefaultPerspective(type, componentId, deviceType);
  }

  clonePerspective(perspectiveId: any, cloneFrom: any) {
    return this.api.clonePerspective(perspectiveId, cloneFrom);
  }
}
